import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import emailjs from "emailjs-com";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import "../Contact/Contact.css";
import {
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaClock,
  FaPaperPlane,
  FaShieldAlt,
  FaAward,
  FaCheckCircle,
  FaUserTie,
  FaGlobeAmericas,
  FaCalendarAlt,
  FaUsers,
  FaStar,
  FaPlane,
  FaHotel,
  FaUmbrellaBeach,
  FaCertificate,
  FaChevronDown,
  FaEnvelope,
  FaWhatsapp,
  FaVideo,
  FaCommentAlt,
  FaMoneyBillAlt,
} from "react-icons/fa";
import { GiWorld } from "react-icons/gi";
import { TbPlaneInflight } from "react-icons/tb";

const Contact = () => {
  const location = useLocation();

  const navigate = useNavigate();
  const formRef = useRef(null);
  const heroRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    destination: "",
    time: "",
    tripDetails: "",
  });

  // Error state
  const [errors, setErrors] = useState({});

  // Button state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form validity state
  const [isFormValid, setIsFormValid] = useState(false);

  // Interactive states
  const [activeField, setActiveField] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);

  // Destination suggestions
  const destinationSuggestions = [
    "Maldives",
    "Bali",
    "Santorini",
    "Swiss Alps",
    "Japan",
    "Italy",
    "Dubai",
    "Paris",
    "New York",
    "Thailand",
  ];

  // Time options
  const timeOptions = [
    "Morning (9 AM - 12 PM)",
    "Afternoon (12 PM - 5 PM)",
    "Evening (5 PM - 9 PM)",
    "Flexible",
  ];

  // Validate form on data change
  useEffect(() => {
    const isValid =
      formData.fullName.trim() !== "" &&
      !errors.fullName &&
      formData.email.trim() !== "" &&
      !errors.email &&
      formData.tripDetails.trim() !== "" &&
      !errors.tripDetails;

    setIsFormValid(isValid);
    setCharacterCount(formData.tripDetails.length);
  }, [formData, errors]);

  // Scroll progress effect
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.pageYOffset / totalHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    validateField(name, value);
  };

  // Field validation
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "fullName":
        if (value.trim() === "") {
          error = "Name is required";
        } else if (!/^[A-Za-z\s]+$/.test(value)) {
          error = "Name should contain only alphabets";
        }
        break;

      case "email":
        if (value.trim() === "") {
          error = "Email is required";
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
          error = "Please enter a valid email address";
        }
        break;

      case "phone":
        if (value.trim() !== "" && !/^[\d\s\-\+\(\)]+$/.test(value)) {
          error = "Please enter a valid phone number";
        }
        break;

      case "tripDetails":
        if (value.trim() === "") {
          error = "Please tell us about your trip";
        } else if (value.length < 50) {
          error = "Please provide more details (minimum 50 characters)";
        }
        break;

      default:
        break;
    }

    setErrors({
      ...errors,
      [name]: error,
    });
  };

  useEffect(() => {
    if (location.hash === "#form-c") {
      const el = document.getElementById("form-c");
      el?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  useEffect(() => {
    if (location.state?.scrollTo === "fm") {
      const el = document.getElementById("fm");
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  }, [location]);
  // Handle form submission with enhanced feedback
  const handleSubmit = (e) => {
    e.preventDefault();

    // Final validation check
    if (!isFormValid) {
      Swal.fire({
        title: "Incomplete Form",
        text: "Please fill in all required fields correctly.",
        icon: "warning",
        confirmButtonText: "OK",
        background: "#0a2647",
        color: "#fff",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    // Disable submit button
    setIsSubmitting(true);

    // EmailJS configuration
    const serviceId = "service_src72qi";
    const templateId = "template_utxpfp5";
    const userId = "X5UqVmxpTiGClhNfI";

    // Prepare template parameters
    const templateParams = {
      from_name: formData.fullName,
      from_email: formData.email,
      phone: formData.phone || "Not provided",
      destination: formData.destination,
      time: formData.time || "Not specified",
      message: formData.tripDetails,
      company_name: "Click4Flight",
    };

    // Send email
    emailjs
      .send(serviceId, templateId, templateParams, userId)
      .then((response) => {
        console.log("SUCCESS!", response.status, response.text);

        // Show enhanced success animation
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);

        // Show success alert with options
        Swal.fire({
          title: "✈️ Inquiry Sent!",
          html: `
            <div style="text-align: left; padding: 20px;">
                <p style="margin-bottom: 10px;"><strong>Thank you, ${
                  formData.fullName
                }!</strong></p>
                <p style="margin-bottom: 15px;">Your travel enquiry has been submitted successfully.</p>
                <div style="background: rgba(37, 99, 235, 0.1); padding: 15px; border-radius: 10px; margin: 15px 0;">
                    <p style="margin: 5px 0;"><strong>Destination:</strong> ${
                      formData.destination || "Not specified"
                    }</p>
                    <p style="margin: 5px 0;"><strong>Reference:</strong> BS-${Date.now()
                      .toString()
                      .slice(-6)}</p>
                </div>
                <p>A travel specialist will contact you within 24 hours.</p>
            </div>
          `,
          icon: "success",
          showCancelButton: true,
          confirmButtonText: "View Thank You Page",
          cancelButtonText: "Stay on Page",
          background: "#0a2647",
          color: "#fff",
          confirmButtonColor: "#2563eb",
          cancelButtonColor: "#64748b",
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/thankyou");
          }
        });

        // Reset form and button state
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          destination: "",
          time: "",
          tripDetails: "",
        });
        setIsSubmitting(false);
      })
      .catch((err) => {
        console.log("FAILED...", err);

        // Show error alert
        Swal.fire({
          title: "Connection Error",
          text: "Please try again or call us directly at 000 0000 0000",
          icon: "error",
          confirmButtonText: "OK",
          background: "#0a2647",
          color: "#fff",
          confirmButtonColor: "#2563eb",
        });

        // Re-enable submit button
        setIsSubmitting(false);
      });
  };

  const handleCall = () => {
    Swal.fire({
      title: "Call Click4Flight?",
      text: "You'll be connected to our travel specialist team.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Call Now",
      cancelButtonText: "Cancel",
      background: "#0a2647",
      color: "#fff",
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#64748b",
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.href = "tel:02033557350  ";
      }
    });
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      "Hello Click4Flight! I'd like to inquire about travel options."
    );
    window.open(`https://wa.me/447700172170?text=${message}`, "_blank");
  };

  const handleVideoCall = () => {
    Swal.fire({
      title: "Schedule Video Consultation",
      text: "Book a personalized video call with our travel experts",
      icon: "info",
      confirmButtonText: "Schedule Now",
      background: "#0a2647",
      color: "#fff",
      confirmButtonColor: "#2563eb",
    });
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSuggestionClick = (suggestion) => {
    setFormData({
      ...formData,
      destination: suggestion,
    });
  };

  const handleTimeSelect = (time) => {
    setFormData({
      ...formData,
      time: time,
    });
    setShowTimeDropdown(false);
  };


   const handleContactClick = () => {
    navigate("/contactus");
  };
 const handleClick = () => {
  const fmSection = document.getElementById("fm");
  if (fmSection) {
    fmSection.scrollIntoView({ behavior: "smooth" });
  }
};


  return (
    <>
      {/* Scroll Progress Bar */}
      <div className="bluesky-scroll-progress">
        <div
          className="bluesky-scroll-progress-bar"
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>

      {/* Hero Section */}
      <div className="bluesky-hero-section" ref={heroRef}>
        <div className="bluesky-hero-overlay">
          <div className="bluesky-hero-content">
            <h1 className="bluesky-hero-title">
              <span className="bluesky-hero-title-line">Connect</span>
              <span className="bluesky-hero-title-line bluesky-hero-highlight">
                Click4Flight
              </span>
            </h1>
            <p className="bluesky-hero-subtitle">
              Where Your Dream Journey Takes Flight
            </p>
            <div className="bluesky-hero-actions">
              <button
                className="bluesky-hero-btn bluesky-hero-btn-primary"
                onClick={() => {
                  // Set flag
                  localStorage.setItem("autoOpenBook", "true");

                  // Get today's date in YYYY-MM-DD
                  const today = new Date();
                  const formattedToday = today.toISOString().split("T")[0];

                  // Navigate with today's date
                  navigate(
                    `/flight-result?origin=LON&destination=&departureDate=${formattedToday}&openBook=true`
                  );
                }}
              >
                Plan Your Trip
              </button>

              <button
                onClick={handleCall}
                className="bluesky-hero-btn bluesky-hero-btn-secondary"
              >
                <FaPhoneAlt /> Call Expert
              </button>
            </div>
          </div>
        </div>

        {/* Floating elements */}
        <div className="bluesky-floating-plane">
          <TbPlaneInflight />
        </div>
        <div className="bluesky-floating-globe">
          <GiWorld />
        </div>
      </div>

      {/* Main Contact Section */}
      <div id="fm" className="bluesky-contact-section" ref={formRef}>
        <div className="bluesky-container">
          <div className="bluesky-contact-wrapper">
            {/* Contact Form - Enhanced Design */}
            <div className="bluesky-form-container-enhanced">
              <div className="bluesky-form-header-enhanced">
                <div className="bluesky-form-title-wrapper">
                  <h2 className="bluesky-form-title-enhanced">
                    <span className="bluesky-form-title-main">
                      Plan Your Next Adventure <br />{" "}
                      <p className="bluesky-form-subtitle-enhanced">
                        Fill out this form and receive a personalized itinerary
                        within 24 hours
                      </p>
                    </span>
                    <div id="form-c" className="bluesky-form-stats">
                      <div className="bluesky-form-stat">
                        <div className="bluesky-stat-number">98%</div>
                        <div className="bluesky-stat-label">Satisfaction</div>
                      </div>
                      <div className="bluesky-form-stat">
                        <div className="bluesky-stat-number">24h</div>
                        <div className="bluesky-stat-label">Response</div>
                      </div>
                      <div className="bluesky-form-stat">
                        <div className="bluesky-stat-number">150+</div>
                        <div className="bluesky-stat-label">Destinations</div>
                      </div>
                    </div>
                  </h2>
                </div>
              </div>

              {/* Success Animation Overlay */}
              {showSuccess && (
                <div className="bluesky-success-overlay-enhanced">
                  <div className="bluesky-success-content">
                    <div className="bluesky-success-icon-enhanced">
                      <svg className="bluesky-checkmark" viewBox="0 0 52 52">
                        <circle
                          className="bluesky-checkmark-circle"
                          cx="26"
                          cy="26"
                          r="25"
                          fill="none"
                        />
                        <path
                          className="bluesky-checkmark-check"
                          fill="none"
                          d="M14.1 27.2l7.1 7.2 16.7-16.8"
                        />
                      </svg>
                    </div>
                    <h3 className="bluesky-success-title">
                      Enquiry Sent Successfully!
                    </h3>
                    <p className="bluesky-success-message">
                      Our travel experts will contact you within 24 hours
                    </p>
                  </div>
                </div>
              )}

              <form className="bluesky-form-enhanced" onSubmit={handleSubmit}>
                <div className="bluesky-form-grid">
                  {/* Full Name */}
                  <div className="bluesky-form-group-enhanced">
                    <div className="bluesky-input-wrapper">
                      <input
                        type="text"
                        name="fullName"
                        className={`bluesky-input-enhanced ${
                          errors.fullName ? "error" : ""
                        } ${activeField === "fullName" ? "active" : ""}`}
                        placeholder="Full Name *"
                        value={formData.fullName}
                        onChange={handleChange}
                        onFocus={() => setActiveField("fullName")}
                        onBlur={() => setActiveField(null)}
                      />
                      <div className="bluesky-input-border"></div>
                    </div>
                    {errors.fullName && (
                      <p className="bluesky-error-message-enhanced">
                        <span className="bluesky-error-icon">!</span>
                        {errors.fullName}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div className="bluesky-form-group-enhanced">
                    <div className="bluesky-input-wrapper">
                      <input
                        type="email"
                        name="email"
                        className={`bluesky-input-enhanced ${
                          errors.email ? "error" : ""
                        } ${activeField === "email" ? "active" : ""}`}
                        placeholder="Email Address *"
                        value={formData.email}
                        onChange={handleChange}
                        onFocus={() => setActiveField("email")}
                        onBlur={() => setActiveField(null)}
                      />
                      <div className="bluesky-input-border"></div>
                    </div>
                    {errors.email && (
                      <p className="bluesky-error-message-enhanced">
                        <span className="bluesky-error-icon">!</span>
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="bluesky-form-group-enhanced">
                    <div className="bluesky-input-wrapper">
                      <input
                        type="tel"
                        name="phone"
                        className={`bluesky-input-enhanced ${
                          errors.phone ? "error" : ""
                        } ${activeField === "phone" ? "active" : ""}`}
                        placeholder="Phone Number"
                        value={formData.phone}
                        onChange={handleChange}
                        onFocus={() => setActiveField("phone")}
                        onBlur={() => setActiveField(null)}
                      />
                      <div className="bluesky-input-border"></div>
                    </div>
                  </div>

                  {/* Time Dropdown */}
                  <div className="bluesky-form-group-enhanced">
                    <div className="bluesky-input-wrapper">
                      <input
                        type="text"
                        name="time"
                        className={`bluesky-input-enhanced ${
                          activeField === "time" ? "active" : ""
                        }`}
                        placeholder="Preferred Contact Time"
                        value={formData.time}
                        onChange={handleChange}
                        onFocus={() => {
                          setActiveField("time");
                          setShowTimeDropdown(true);
                        }}
                        onBlur={() => {
                          setActiveField(null);
                          setTimeout(() => setShowTimeDropdown(false), 200);
                        }}
                        readOnly
                      />
                      <div className="bluesky-input-border"></div>
                      <div
                        className="bluesky-select-arrow"
                        onClick={() => setShowTimeDropdown(!showTimeDropdown)}
                        style={{ cursor: "pointer" }}
                      >
                        <FaChevronDown />
                      </div>
                    </div>
                    {showTimeDropdown && (
                      <div className="bluesky-time-dropdown">
                        {timeOptions.map((time, index) => (
                          <div
                            key={index}
                            className="bluesky-time-option"
                            onClick={() => handleTimeSelect(time)}
                          >
                            {time}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Destination */}
                  <div className="bluesky-form-group-enhanced bluesky-form-full-width">
                    <div className="bluesky-input-wrapper">
                      <input
                        type="text"
                        name="destination"
                        className={`bluesky-input-enhanced ${
                          errors.destination ? "error" : ""
                        } ${activeField === "destination" ? "active" : ""}`}
                        placeholder="Dream Destination *"
                        value={formData.destination}
                        onChange={handleChange}
                        onFocus={() => setActiveField("destination")}
                        onBlur={() => setActiveField(null)}
                        list="destination-suggestions"
                      />
                      <div className="bluesky-input-border"></div>
                    </div>

                    {/* Destination Suggestions
                    <div className="bluesky-suggestions-enhanced">
                      <div className="bluesky-suggestions-tags">
                        {destinationSuggestions
                          .slice(0, 6)
                          .map((suggestion, index) => (
                            <button
                              key={index}
                              type="button"
                              className="bluesky-suggestion-tag-enhanced"
                              onClick={() => handleSuggestionClick(suggestion)}
                            >
                              <span className="bluesky-suggestion-icon">
                                📍
                              </span>
                              {suggestion}
                            </button>
                          ))}
                      </div>
                    </div>
                     */}

                    {errors.destination && (
                      <p className="bluesky-error-message-enhanced">
                        <span className="bluesky-error-icon">!</span>
                        {errors.destination}
                      </p>
                    )}
                  </div>

                  {/* Trip Details */}
                  <div className="bluesky-form-group-enhanced bluesky-form-full-width">
                    <div className="bluesky-textarea-header">
                      <span className="bluesky-char-count-enhanced">
                        <span className="bluesky-char-current">
                          {characterCount}
                        </span>
                        <span className="bluesky-char-separator">/</span>
                        <span className="bluesky-char-max">500</span>
                      </span>
                    </div>
                    <div className="bluesky-textarea-wrapper">
                      <textarea
                        name="tripDetails"
                        className={`bluesky-textarea-enhanced ${
                          errors.tripDetails ? "error" : ""
                        } ${activeField === "tripDetails" ? "active" : ""}`}
                        rows="6"
                        placeholder="Tell us about your ideal trip and preferences…"
                        value={formData.tripDetails}
                        onChange={handleChange}
                        onFocus={() => setActiveField("tripDetails")}
                        onBlur={() => setActiveField(null)}
                        maxLength="500"
                      ></textarea>
                      <div className="bluesky-input-border"></div>
                    </div>
                    {errors.tripDetails && (
                      <p className="bluesky-error-message-enhanced">
                        <span className="bluesky-error-icon">!</span>
                        {errors.tripDetails}
                      </p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="bluesky-form-footer-enhanced">
                  <div className="bluesky-submit-section">
                    <button
                      type="submit"
                      className={`bluesky-submit-btn-enhanced ${
                        isSubmitting ? "submitting" : ""
                      } ${!isFormValid ? "disabled" : ""}`}
                      disabled={isSubmitting || !isFormValid}
                    >
                      <span className="bluesky-submit-content">
                        {isSubmitting ? (
                          <>
                            <span className="bluesky-spinner-enhanced"></span>
                            <span className="bluesky-submit-text">
                              Crafting Your Proposal...
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="bluesky-submit-icon">
                              <FaPaperPlane />
                            </span>
                            <span className="bluesky-submit-text">
                              Send Travel Enquiry
                            </span>
                           
                          </>
                        )}
                      </span>
                      <div className="bluesky-submit-glow"></div>
                    </button>
                  </div>
                </div>
              </form>
            </div>

            {/* Connect With Our Travel Experts - Compact Fancy Section */}
            <div className="bluesky-experts-compact-section">
              <div className="bluesky-experts-compact-header">
                <div className="bluesky-experts-compact-title-wrapper">
                  <h2 className="bluesky-experts-compact-title">
                    <span className="bluesky-experts-compact-icon">
                      <FaUserTie />
                    </span>
                    Connect With Our Travel Experts
                  </h2>
                  <p className="bluesky-experts-compact-subtitle">
                    Multiple ways to start your luxury journey
                  </p>
                </div>
                <div className="bluesky-experts-compact-badge">
                  <FaShieldAlt /> 24/7 Premium Support
                </div>
              </div>

              <div className="bluesky-experts-compact-grid">
                {/* Call Expert */}
                <div className="bluesky-expert-compact-card bluesky-expert-compact-call">
                  <div className="bluesky-expert-compact-card-inner">
                    <div className="bluesky-expert-compact-icon-wrapper">
                      <div className="bluesky-expert-compact-icon" onClick={handleCall}>
                        <FaPhoneAlt />
                      </div>
                      <div className="bluesky-expert-compact-pulse"></div>
                    </div>
                    <div className="bluesky-expert-compact-content">
                      <h3 className="bluesky-expert-compact-name">
                        Call Expert
                      </h3>
                      <p className="bluesky-expert-compact-details">
                        <span className="bluesky-expert-compact-number">
                          02033557350  
                        </span>
                        <span className="bluesky-expert-compact-hours">
                          <FaClock /> Mon-Sat, 9am-6pm
                        </span>
                      </p>
                    </div>
                   
                  </div>
                  <div className="bluesky-expert-compact-ribbon">
                    Instant Connect
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="bluesky-expert-compact-card bluesky-expert-compact-whatsapp">
                  <div className="bluesky-expert-compact-card-inner">
                    <div className="bluesky-expert-compact-icon-wrapper">
                      <div className="bluesky-expert-compact-icon" onClick={handleWhatsApp}>
                        <FaWhatsapp />
                      </div>
                      <div className="bluesky-expert-compact-pulse"></div>
                    </div>
                    <div className="bluesky-expert-compact-content">
                      <h3 className="bluesky-expert-compact-name">WhatsApp</h3>
                      <p className="bluesky-expert-compact-details">
                        <span className="bluesky-expert-compact-number">
                         +447700172170
                        </span>
                        <span className="bluesky-expert-compact-hours">
                          <FaClock /> 24/7 Chat Support
                        </span>
                      </p>
                    </div>
                   
                  </div>
                  <div className="bluesky-expert-compact-ribbon">
                    Quick Response
                  </div>
                </div>

                {/* Email */}
                <div className="bluesky-expert-compact-card bluesky-expert-compact-email">
                  <div className="bluesky-expert-compact-card-inner">
                    <div className="bluesky-expert-compact-icon-wrapper">
                      <a  href="mailto:info@click4flight.co.uk" className="bluesky-expert-compact-icon">
                        <FaEnvelope />
                      </a>
                      <div className="bluesky-expert-compact-glow"></div>
                    </div>
                    <div className="bluesky-expert-compact-content">
                      <h3 className="bluesky-expert-compact-name">Email</h3>
                      <p className="bluesky-expert-compact-details">
                        <span className="bluesky-expert-compact-email">
                          info@click4flight.co.uk
                        </span>
                        <span className="bluesky-expert-compact-hours">
                          <FaClock /> Reply within 2h
                        </span>
                      </p>
                    </div>
                  
                  </div>
                  <div className="bluesky-expert-compact-ribbon">
                    Detailed Quotes
                  </div>
                </div>

                {/* Visit */}
                <div className="bluesky-expert-compact-card bluesky-expert-compact-visit">
                  <div className="bluesky-expert-compact-card-inner" onClick={scrollToForm}>
                    <div className="bluesky-expert-compact-icon-wrapper">
                      <div className="bluesky-expert-compact-icon" >
                        <FaMapMarkerAlt />
                      </div>
                      <div className="bluesky-expert-compact-glow"></div>
                    </div>
                    <div className="bluesky-expert-compact-content">
                      <h3 className="bluesky-expert-compact-name">Visit Us</h3>
                      <p className="bluesky-expert-compact-details">
                        <span className="bluesky-expert-compact-address">
                          London HQ
                        </span>
                        <span className="bluesky-expert-compact-hours">
                          <FaClock /> By Appointment
                        </span>
                      </p>
                    </div>
                  
                  </div>
                  <div className="bluesky-expert-compact-ribbon">
                    Personal Consult
                  </div>
                </div>
              </div>

              {/* Trust Badges - Compact */}
              <div className="bluesky-trust-compact-section">
                <div className="bluesky-trust-compact-grid">
                  <div className="bluesky-trust-compact-badge">
                    <div className="bluesky-trust-compact-icon">
                      <FaShieldAlt />
                    </div>
                    <div className="bluesky-trust-compact-text">
                      <strong>ATOL Protected</strong>
                      <span>Financial Security</span>
                    </div>
                  </div>
                  <div className="bluesky-trust-compact-badge">
                    <div className="bluesky-trust-compact-icon">
                      <FaAward />
                    </div>
                    <div className="bluesky-trust-compact-text">
                      <strong>Award Winning</strong>
                      <span>5★ Service</span>
                    </div>
                  </div>
                  {/*--
                  <div className="bluesky-trust-compact-badge">
                    <div className="bluesky-trust-compact-icon">
                      <FaCertificate />
                    </div>
                    <div className="bluesky-trust-compact-text">
                      <strong>IATA</strong>
                      <span>Safety & Security</span>
                    </div>
                  </div>
                  --*/}
                  <div className="bluesky-trust-compact-badge">
                    <div className="bluesky-trust-compact-icon">
                      <FaStar />
                    </div>
                    <div className="bluesky-trust-compact-text">
                      <strong>98% Rating</strong>
                      <span>Client Satisfaction</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive Map Section */}
      <div className="bluesky-map-section">
        <div className="bluesky-container">
          <div className="bluesky-map-header">
            <h2 className="bluesky-map-title">Find Us Here</h2>
            <p className="bluesky-map-subtitle">
              Our London headquarters in the heart of the City
            </p>
          </div>
          <div className="bluesky-map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d10623.247432647555!2d-0.106978700759122!3d51.52682861287587!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761ca671a6cfb1%3A0x3adb2930f9039227!2s124%20City%20Rd%2C%20London%20EC1V%202NX%2C%20UK!5e0!3m2!1sen!2sin!4v1746705552122!5m2!1sen!2sin"
              className="bluesky-map"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Click4Flight London Office"
            ></iframe>
           
          </div>
        </div>
      </div>

      {/* Enhanced Why Choose Us Section */}
      <section className="bluesky-why-section">
        <div className="bluesky-container">
          <div className="bluesky-why-header">
            <h2 className="bluesky-why-title">Why Choose Click4Flight?</h2>
            <p className="bluesky-why-subtitle">
              Experience the difference with our premium travel planning
              services
            </p>
          </div>
          <div className="bluesky-why-grid">
            {[
              {
                icon: <FaUserTie />,
                title: "Personal Travel Designer",
                desc: "Dedicated expert who learns your preferences",
              },
              {
                icon: <FaShieldAlt />,
                title: "Full Financial Protection",
                desc: "ATOL protected for complete peace of mind",
              },
              {
                icon: <FaAward />,
                title: "Award-Winning Service",
                desc: "Consistently rated 5-stars by our clients",
              },
              {
                icon: <FaCheckCircle />,
                title: "Tailored Experiences",
                desc: "100% customized itineraries, no template trips",
              },
              {
                icon: <FaPlane />,
                title: "VIP Airport Services",
                desc: "Fast-track, lounges, and priority services",
              },
              {
                icon: <FaHotel />,
                title: "Premium Accommodations",
                desc: "Curated selection of luxury hotels & resorts",
              },
              {
                icon: <FaStar />,
                title: "24/7 Concierge Support",
                desc: "Round-the-clock support during your travels",
              },
              {
                icon: <FaGlobeAmericas />,
                title: "Global Insider Access",
                desc: "Exclusive experiences not available to public",
              },
            ].map((item, index) => (
              <div className="bluesky-why-card" key={index}>
                <div className="bluesky-why-icon-wrapper">
                  <div className="bluesky-why-icon">{item.icon}</div>
                  <div className="bluesky-why-number">0{index + 1}</div>
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Trust Indicators */}
          <div className="bluesky-trust-indicators">
            <div className="bluesky-trust-item">
              <div className="bluesky-trust-value">98%</div>
              <div className="bluesky-trust-label">Client Satisfaction</div>
            </div>
            <div className="bluesky-trust-item">
              <div className="bluesky-trust-value">24h</div>
              <div className="bluesky-trust-label">Response Time</div>
            </div>
            <div className="bluesky-trust-item">
              <div className="bluesky-trust-value">£10M+</div>
              <div className="bluesky-trust-label">ATOL Protected</div>
            </div>
            <div className="bluesky-trust-item">
              <div className="bluesky-trust-value">5★</div>
              <div className="bluesky-trust-label">Trustpilot Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Action Button */}
      <button className="bluesky-fab" onClick={handleCall}>
        <FaPhoneAlt />
        <span className="bluesky-fab-tooltip">Call Now: 02033557350  </span>
      </button>

      {/* Success Confetti Effect */}
      {showSuccess && (
        <div className="bluesky-confetti">
          {[...Array(50)].map((_, i) => (
            <div key={i} className="bluesky-confetti-piece"></div>
          ))}
        </div>
      )}
    </>
  );
};

export default Contact;
